import { Chip, IconButton, Stack, Typography } from "@mui/material";
import { Edit20Px, Service20Px } from "@qubit/autoparts";

import { FlaggedReasonType, flaggedReasonIconMap } from "~/lib/bin";
import { capitalizeFirstLetter } from "~/lib/helpers";
import { parseProductTempZone, tempZoneIconMap } from "~/lib/tempZone";
import { TemperatureZone } from "~/types/api";

type Props = {
  tempZone?: TemperatureZone;
  flaggedReasons: FlaggedReasonType[];
  onEdit: () => void;
};

export function MaintenanceSessionConfigChip({
  tempZone,
  flaggedReasons,
  onEdit
}: Props) {
  const normalizedZone = parseProductTempZone(tempZone);

  const TempZoneIcon = normalizedZone ? tempZoneIconMap[normalizedZone] : null;
  const zoneLabel = capitalizeFirstLetter(tempZone?.toLowerCase() ?? "");

  let FlagIcon: React.FC<React.SVGProps<SVGSVGElement>> | null = null;
  let flagLabel: string | null = null;

  if (flaggedReasons.length > 1) {
    FlagIcon = Service20Px;
    flagLabel = "Multi";
  } else if (flaggedReasons.length === 1) {
    FlagIcon = flaggedReasonIconMap[flaggedReasons[0]];
    flagLabel = capitalizeFirstLetter(flaggedReasons[0]);
  }

  return (
    <Chip
      aria-label={`${zoneLabel ? `${zoneLabel} temperature zone` : ""}${flagLabel ? `, ${flagLabel} flag type` : ""}`}
      variant="outlined"
      label={
        <Stack direction="row" alignItems="center" spacing={1}>
          {TempZoneIcon && <TempZoneIcon width={20} height={20} />}
          <Typography sx={{ fontSize: "1.25rem" }}>{zoneLabel}</Typography>
          {FlagIcon && flagLabel && (
            <>
              {zoneLabel && (
                <Typography
                  component="span"
                  sx={{
                    fontSize: "1.25rem",
                    color: "text.secondary",
                    px: 0.25
                  }}
                >
                  ·
                </Typography>
              )}
              <FlagIcon width={20} height={20} />
              <Typography sx={{ fontSize: "1.25rem" }}>{flagLabel}</Typography>
            </>
          )}
          <IconButton
            onClick={(event) => {
              event.stopPropagation();
              onEdit();
            }}
            aria-label="edit"
            sx={{
              width: 36,
              height: 36,
              bgcolor: "background.gray",
              color: "text.primary",
              "&:hover": {
                bgcolor: "grey.300"
              }
            }}
          >
            <Edit20Px width={20} height={20} />
          </IconButton>
        </Stack>
      }
      sx={{
        height: "auto",
        borderRadius: 9999,
        borderColor: "grey.300",
        bgcolor: "background.paper",
        pl: 2,
        pr: 1,
        py: 0.75,
        "& .MuiChip-label": {
          display: "flex",
          alignItems: "center",
          px: 0,
          py: 0
        }
      }}
    />
  );
}
